import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { PurRecord } from "react-lib/apis/PUR/purApi";
import {
  canUsePurApprovalPilot, checkPurApprovalPermissions, performPurApproval, preparePurApproval,
  PurApprovalAttempt, PurApprovalError, PurApprovalPermission, purApprovalNextAction,
  purApprovalPermissionNames, purApprovalTargetStatus, reconcilePurApproval,
} from "react-lib/apis/PUR/purApproval";
import { PurDraftKind } from "react-lib/apis/PUR/purDraftCreatePilot";
import { PurClientConfig } from "react-lib/apis/PUR/purRead";

import { purErrorMessageId } from "./purPresentation";

interface PurApprovalActionsProps {
  client: PurClientConfig;
  kind: PurDraftKind;
  record: PurRecord;
  onUpdated: (record: PurRecord) => void;
  onLockChange: (locked: boolean) => void;
}
type Phase = "idle" | "reviewing" | "confirming" | "saving" | "unknown" | "reconciling";

const PurApprovalActions: React.FC<PurApprovalActionsProps> = ({ client, kind, record, onUpdated, onLockChange }) => {
  const intl = useIntl();
  const [phase, setPhase] = useState<Phase>("idle");
  const [permission, setPermission] = useState<PurApprovalPermission | null>(null);
  const [checking, setChecking] = useState(false);
  const [errorId, setErrorId] = useState<string | null>(null);
  const [noticeId, setNoticeId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<PurApprovalAttempt | null>(null);
  const active = useRef(true);
  const busy = useRef(false);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const eligible = canUsePurApprovalPilot(client, kind, record);
  const action = purApprovalNextAction(kind, record.status);
  const names = purApprovalPermissionNames(kind);
  const permitted = Boolean(permission?.resource && permission[action === "SUBMIT" ? "submit" : "approve"]);
  const locked = phase !== "idle";
  const pendingStatus = kind === "materialRequests" ? "SUBMITTED" : "PENDING_APPROVAL";

  useEffect(() => { onLockChange(locked); }, [locked, onLockChange]);
  useEffect(() => {
    active.current = true;
    const warn = (event: BeforeUnloadEvent) => {
      if (["saving", "unknown", "reconciling"].includes(phaseRef.current)) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => { active.current = false; onLockChange(false); window.removeEventListener("beforeunload", warn); };
  }, [onLockChange]);
  useEffect(() => {
    if (!eligible || !action) return;
    let current = true;
    setChecking(true); setPermission(null);
    checkPurApprovalPermissions(client, kind)
      .then((value) => { if (current) setPermission(value); })
      .catch(() => { if (current) setErrorId("MEERP_APPROVAL_CHECK_ERROR"); })
      .finally(() => { if (current) setChecking(false); });
    return () => { current = false; };
  }, [client, kind, eligible, action]);

  const check = async () => {
    if (busy.current || locked || checking) return;
    setChecking(true); setErrorId(null);
    try { const value = await checkPurApprovalPermissions(client, kind); if (active.current) setPermission(value); }
    catch { if (active.current) setErrorId("MEERP_APPROVAL_CHECK_ERROR"); }
    finally { if (active.current) setChecking(false); }
  };
  const review = async () => {
    if (busy.current || locked || checking || !eligible || !action || !permitted) return;
    busy.current = true; setPhase("reviewing"); setErrorId(null); setNoticeId(null);
    try {
      const value = await preparePurApproval(client, kind, record.id, action);
      if (active.current) { setAttempt(value); setPhase("confirming"); onUpdated(value.record); }
    } catch (error: unknown) {
      if (active.current) { setPhase("idle"); setErrorId(error instanceof PurApprovalError ? error.messageId : purErrorMessageId(error)); }
    } finally { busy.current = false; }
  };
  const confirm = async () => {
    if (busy.current || phase !== "confirming" || !attempt) return;
    busy.current = true; setPhase("saving"); setErrorId(null);
    try {
      const updated = await performPurApproval(client, attempt, () => active.current);
      if (active.current) { setPhase("idle"); setAttempt(null); setNoticeId("MEERP_APPROVAL_SUCCESS"); onUpdated(updated); }
    } catch (error: unknown) {
      if (active.current) {
        const known = error instanceof PurApprovalError && error.outcome !== "unknown";
        setPhase(known ? "idle" : "unknown");
        if (known) { setAttempt(null); setPermission(null); }
        setErrorId(error instanceof PurApprovalError ? error.messageId : "MEERP_APPROVAL_UNKNOWN");
      }
    } finally { busy.current = false; }
  };
  const reconcile = async () => {
    if (busy.current || phase !== "unknown" || !attempt) return;
    busy.current = true; setPhase("reconciling");
    try {
      const updated = await reconcilePurApproval(client, attempt);
      if (active.current) {
        // Still at the starting state: a timed-out PATCH may yet commit. Stay locked.
        if (updated.status === attempt.record.status) {
          setPhase("unknown"); setErrorId("MEERP_APPROVAL_NOT_SETTLED");
        } else {
          setPhase("idle"); setAttempt(null); setErrorId(null); setNoticeId("MEERP_APPROVAL_OBSERVED"); onUpdated(updated);
        }
      }
    } catch { if (active.current) { setPhase("unknown"); setErrorId("MEERP_APPROVAL_UNKNOWN"); } }
    finally { busy.current = false; }
  };

  return <section className="card-meerp__approval" aria-label={intl.formatMessage({ id: "MEERP_APPROVAL_SECTION" })}>
    <h4>{intl.formatMessage({ id: "MEERP_APPROVAL_SECTION" })}</h4>
    <ol className="card-meerp__approval-steps" aria-label={intl.formatMessage({ id: "MEERP_APPROVAL_FLOW" })}>
      {["DRAFT", pendingStatus, "APPROVED"].map((status) => <li key={status} aria-current={record.status === status ? "step" : undefined}>
        {intl.formatMessage({ id: `MEERP_STATUS_${status}` })} <code>{status}</code>
      </li>)}
    </ol>
    <p>{intl.formatMessage({ id: kind === "materialRequests" ? "MEERP_APPROVAL_MR_NOTE" : "MEERP_APPROVAL_PO_NOTE" })}</p>
    <p className="card-meerp__muted">{intl.formatMessage({ id: "MEERP_APPROVAL_PILOT_NOTE" })}</p>
    {!eligible && <p role="status">{intl.formatMessage({ id: "MEERP_APPROVAL_SCOPE_ERROR" })}</p>}
    {eligible && action && <>
      <p>{intl.formatMessage({ id: "MEERP_APPROVAL_PERMISSIONS" })}</p>
      <ul>{[names.resource, names[action === "SUBMIT" ? "submit" : "approve"]].map((name) => <li key={name}><code>{name}</code></li>)}</ul>
      {checking && <p role="status">{intl.formatMessage({ id: "MEERP_APPROVAL_CHECKING" })}</p>}
      {!checking && permission && !permitted && <p role="alert">{intl.formatMessage({ id: "MEERP_APPROVAL_PERMISSION_MISSING" })}</p>}
      <div className="card-meerp__toolbar">
        <button className="card-meerp__button" type="button" disabled={locked || checking} onClick={check}>{intl.formatMessage({ id: "MEERP_APPROVAL_CHECK" })}</button>
        <button className="card-meerp__button" type="button" disabled={locked || checking || !permitted || !record.items.length} onClick={review}>
          {intl.formatMessage({ id: action === "SUBMIT" ? "MEERP_ACTION_SUBMIT_APPROVAL" : kind === "materialRequests" ? "MEERP_APPROVAL_APPROVE_MR" : "MEERP_APPROVAL_APPROVE_PO" })}
        </button>
      </div>
    </>}
    {noticeId && <p role="status" className="card-meerp__message">{intl.formatMessage({ id: noticeId }, { number: record.number, status: intl.formatMessage({ id: `MEERP_STATUS_${record.status}` }) })}</p>}
    {errorId && <p role="alert" className="card-meerp__error-text">{intl.formatMessage({ id: errorId })}</p>}
    {phase === "reviewing" && <p role="status">{intl.formatMessage({ id: "MEERP_APPROVAL_REVIEWING" })}</p>}
    {phase === "confirming" && attempt && <div className="card-meerp__approval-confirm" role="group" aria-label={intl.formatMessage({ id: "MEERP_APPROVAL_CONFIRM_TITLE" })}>
      <h4>{intl.formatMessage({ id: "MEERP_APPROVAL_CONFIRM_TITLE" })}</h4>
      <p>{intl.formatMessage({ id: "MEERP_APPROVAL_ACTOR" }, { id: attempt.actorUserId })}</p>
      <p>{intl.formatMessage({ id: "MEERP_APPROVAL_CONFIRM_NOTE" }, { number: attempt.record.number, id: attempt.record.id,
        from: intl.formatMessage({ id: `MEERP_STATUS_${attempt.record.status}` }), to: intl.formatMessage({ id: `MEERP_STATUS_${purApprovalTargetStatus(kind, attempt.action)}` }), action: attempt.action })}</p>
      <p>{intl.formatMessage({ id: "MEERP_APPROVAL_REVIEW_SUMMARY" }, { count: attempt.record.items.length,
        total: attempt.record.totalAmount || intl.formatMessage({ id: "MEERP_UNKNOWN" }), storage: attempt.record.storageName || attempt.record.storageId })}</p>
      <p>{intl.formatMessage({ id: "MEERP_APPROVAL_NO_STOCK" })}</p>
      <button className="card-meerp__button" type="button" onClick={confirm}>{intl.formatMessage({ id: attempt.action === "SUBMIT" ? "MEERP_APPROVAL_CONFIRM_SUBMIT" : "MEERP_APPROVAL_CONFIRM_APPROVE" })}</button>
      <button type="button" className="card-meerp__button card-meerp__secondary" onClick={() => { setPhase("idle"); setAttempt(null); }}>{intl.formatMessage({ id: "MEERP_DRAFT_RESET_CANCEL" })}</button>
    </div>}
    {phase === "saving" && <p role="status">{intl.formatMessage({ id: "MEERP_APPROVAL_SAVING" })}</p>}
    {["unknown", "reconciling"].includes(phase) && <button className="card-meerp__button" type="button" disabled={phase === "reconciling"} onClick={reconcile}>{intl.formatMessage({ id: "MEERP_APPROVAL_RECONCILE" })}</button>}
    {record.status === "APPROVED" && <p>{intl.formatMessage({ id: kind === "materialRequests" ? "MEERP_APPROVAL_MR_NEXT" : "MEERP_APPROVAL_PO_NEXT" })}</p>}
  </section>;
};
export default PurApprovalActions;
